import FormGenerator, { FormTypes } from '../components/FormGenerator';
import { householdQuestionRenderers } from '../pages/HouseholdAccounts/formQuestions/questionRenderers';
import { venueRegistrationQuestionRenderers } from '../pages/VenueRegistration/formQuestions';
import { samplesBySection } from './form-generator-playground/samples';

const DEBOUNCE_MS = 450;

const rendererMaps = {
	household: householdQuestionRenderers,
	venueRegistration: venueRegistrationQuestionRenderers
};

const debounce = (fn, wait) => {
	let timeoutId;
	return (...args) => {
		window.clearTimeout(timeoutId);
		timeoutId = window.setTimeout(() => fn(...args), wait);
	};
};

const createCardMarkup = (sample, index) => `
	<article class="fg-playground__card js-fg-playground-card" data-sample-index="${index}">
		<header class="fg-playground__card-header">
			<div>
				<h3 class="u-title3 u-mb-1">${sample.title}</h3>
				<p class="u-body2 u-text-grey-lta u-mb-0">${sample.description || ''}</p>
			</div>
			<button type="button" class="lta-btn lta-btn--sm lta-btn--line js-fg-playground-apply">Apply JSON</button>
		</header>
		<div class="fg-playground__grid">
			<div class="fg-playground__preview">
				<p class="u-caption1 u-mb-2">Live preview</p>
				<div class="fg-playground__mount js-fg-playground-mount"></div>
			</div>
			<div class="fg-playground__editor">
				<p class="u-caption1 u-mb-2">Question JSON (editable)</p>
				<textarea class="fg-playground__json js-fg-playground-json" spellcheck="false" aria-label="${sample.title} JSON"></textarea>
				<p class="fg-playground__status js-fg-playground-status u-caption2 u-mt-2" aria-live="polite"></p>
			</div>
		</div>
	</article>
`;

const setStatus = (statusEl, message, isError = false) => {
	if (!statusEl) {
		return;
	}
	statusEl.textContent = message;
	statusEl.classList.toggle('is-error', isError);
};

const mountForm = (card, questions, formConfig = {}, questionRenderers) => {
	const mount = card.querySelector('.js-fg-playground-mount');
	const statusEl = card.querySelector('.js-fg-playground-status');

	if (!mount) {
		return;
	}

	if (card._formGenerator) {
		card._formGenerator.unload();
		card._formGenerator = null;
	}

	mount.innerHTML = '';

	const totalSteps = Math.max(...questions.map((question) => question.step || 1), 1);

	try {
		const instance = new FormGenerator(mount, {
			questions,
			totalSteps,
			formType: FormTypes.SectionForm,
			themeColor: 'color-lta-blue',
			isHideProgress: true,
			isBackButtonEnabled: false,
			hasSeparator: false,
			submitButton: {
				title: 'Continue',
				alwaysVisible: true
			},
			saveAddress: false,
			...(questionRenderers ? { questionRenderers } : {}),
			...formConfig
		});

		instance.onChange(() => {
			setStatus(statusEl, 'Preview updated — answer changed', false);
		});

		card._formGenerator = instance;
		setStatus(statusEl, 'Preview rendered from JSON', false);
	} catch (error) {
		setStatus(statusEl, `Render error: ${error.message}`, true);
		// eslint-disable-next-line no-console
		console.error(error);
	}
};

const applyCardJson = (card, defaultFormConfig = {}, questionRenderers) => {
	const textarea = card.querySelector('.js-fg-playground-json');
	const statusEl = card.querySelector('.js-fg-playground-status');

	if (!textarea) {
		return;
	}

	try {
		const parsed = JSON.parse(textarea.value);
		const questions = Array.isArray(parsed) ? parsed : parsed.questions;
		const formConfig = {
			...defaultFormConfig,
			...(parsed.formConfig || {})
		};

		if (!Array.isArray(questions) || questions.length === 0) {
			throw new Error('JSON must be an array of questions, or `{ "questions": [...] }`');
		}

		const { questionRenderersKey, ...restFormConfig } = formConfig;
		const renderers =
			questionRenderers ||
			(questionRenderersKey ? rendererMaps[questionRenderersKey] : undefined);

		mountForm(card, questions, restFormConfig, renderers);
	} catch (error) {
		setStatus(statusEl, `Invalid JSON: ${error.message}`, true);
	}
};

const initSectionPlayground = (root) => {
	const section = root.dataset.fgSection;
	const samples = samplesBySection[section];

	if (!samples?.length) {
		root.innerHTML = `<p class="u-body1">No samples found for section <code>${section}</code>.</p>`;
		return;
	}

	const defaultRenderersKey = root.dataset.fgRenderers;
	const defaultQuestionRenderers = defaultRenderersKey ? rendererMaps[defaultRenderersKey] : undefined;

	root.innerHTML = samples.map((sample, index) => createCardMarkup(sample, index)).join('');

	const cards = root.querySelectorAll('.js-fg-playground-card');
	cards.forEach((card, index) => {
		const sample = samples[index];
		const textarea = card.querySelector('.js-fg-playground-json');
		const applyButton = card.querySelector('.js-fg-playground-apply');
		const sampleFormConfig = sample.formConfig || {};
		const questionRenderers =
			defaultQuestionRenderers ||
			(sampleFormConfig.questionRenderersKey
				? rendererMaps[sampleFormConfig.questionRenderersKey]
				: undefined);

		textarea.value = JSON.stringify(sample.questions, null, 2);

		const apply = () => applyCardJson(card, sampleFormConfig, questionRenderers);
		const debouncedApply = debounce(apply, DEBOUNCE_MS);

		applyButton?.addEventListener('click', apply);
		textarea.addEventListener('input', debouncedApply);

		apply();
	});
};

const boot = () => {
	document.querySelectorAll('.js-fg-playground-root').forEach((root) => {
		initSectionPlayground(root);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}
